import { useMemo } from "react";
import {
  GroupStep,
  isCanceled,
  isFailed,
  isPassed,
  isRunning,
  isScheduled,
  isSoftFailed,
  isWaitingForDependencies,
  isWaitingForInput,
  Outcome,
  State,
  Step,
  Type,
} from "~/lib/pipeline";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import { MissingStepDependencies } from "~/components/Playground/Node/MissingStepDependencies";
import Emojify from "~/components/shared/Emojify";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { GroupStepCount } from "~/components/build/Show/components/GroupStepCount";
import { StateIcon, StateLabel } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { Build, StepDependencyContext } from "~/components/build/Show/lib/types";
import {
  FilterByOption,
  FILTER_STATE_CONFIG,
  StateOrder,
} from "~/components/build/Show/lib/filterConstants";

type SummaryEntry = {
  key: string;
  label: string;
  matches: (step: Step) => boolean;
  state: State;
  outcome: Outcome | null;
};

// Map FilterByOption to step-level summary entries. "Waiting" expands into
// two rows (dependencies and input) to give more detail in the hovercard.
const ENTRIES_BY_FILTER: Record<FilterByOption, SummaryEntry[]> = {
  [FilterByOption.Failed]: [
    {
      key: "failed",
      ...FILTER_STATE_CONFIG[FilterByOption.Failed],
      label: "failed",
      matches: isFailed,
    },
  ],
  [FilterByOption.Canceled]: [
    {
      key: "canceled",
      ...FILTER_STATE_CONFIG[FilterByOption.Canceled],
      label: "canceled",
      matches: isCanceled,
    },
  ],
  [FilterByOption.Running]: [
    {
      key: "running",
      ...FILTER_STATE_CONFIG[FilterByOption.Running],
      label: "running",
      matches: isRunning,
    },
  ],
  [FilterByOption.Scheduled]: [
    {
      key: "scheduled",
      ...FILTER_STATE_CONFIG[FilterByOption.Scheduled],
      label: "scheduled",
      matches: isScheduled,
    },
  ],
  [FilterByOption.Waiting]: [
    {
      key: "waiting-input",
      label: "waiting for input",
      matches: isWaitingForInput,
      state: State.WaitingForInput,
      outcome: null,
    },
    {
      key: "waiting-deps",
      ...FILTER_STATE_CONFIG[FilterByOption.Waiting],
      label: "waiting for dependencies",
      matches: isWaitingForDependencies,
    },
  ],
  [FilterByOption.SoftFailed]: [
    {
      key: "soft-failed",
      ...FILTER_STATE_CONFIG[FilterByOption.SoftFailed],
      label: "soft failed",
      matches: isSoftFailed,
    },
  ],
  [FilterByOption.Passed]: [
    {
      key: "passed",
      ...FILTER_STATE_CONFIG[FilterByOption.Passed],
      label: "passed",
      matches: isPassed,
    },
  ],
  [FilterByOption.Ignored]: [],
};

const SUMMARY_ORDER = StateOrder.flatMap((filter) => ENTRIES_BY_FILTER[filter]);

interface Props {
  step: GroupStep;
  build: Build;
  missingDependencies: StepDependencyContext["missing_dependencies"];
}

export function GroupStepHoverCard({ step, build, missingDependencies }: Props) {
  const stepsGroupedByParent = useBuildPageStore((state) => state.stepsGroupedByParent);

  const childSteps = useMemo(() => {
    return (stepsGroupedByParent.get(step.uuid) || []).filter(
      (child) => child.type !== Type.Wait && child.state !== State.Ignored,
    );
  }, [stepsGroupedByParent, step.uuid]);

  const summaryRows = useMemo(() => {
    return SUMMARY_ORDER.map((entry) => ({
      ...entry,
      count: childSteps.filter(entry.matches).length,
    })).filter((row) => row.count > 0);
  }, [childSteps]);

  return (
    <HoverCard.Content
      state={step.state}
      outcome={step.outcome}
      missingDependencies={missingDependencies}
      collisionBoundary={document.getElementById("canvas")}
      collisionPadding={10}
    >
      <div className="space-y-3">
        <div className="space-y-2">
          {step.label && (
            <div className="line-clamp-2 break-words font-medium">
              <Emojify text={step.label} />
            </div>
          )}

          <div className="flex items-baseline gap-2">
            <GroupStepCount step={step} />

            <div className="ml-auto flex items-center gap-2">
              <StateLabel {...step} missingDependencies={missingDependencies} />
              <StateIcon
                className={textColorForStep({ ...step, missingDependencies })}
                {...step}
                missingDependencies={missingDependencies}
              />
            </div>
          </div>
        </div>

        {summaryRows.length > 0 && (
          <ul className="text-charcoal-300 -mx-3 space-y-1 border-t border-gray-300 px-3 pt-3 text-sm">
            {summaryRows.map((row) => (
              <li key={row.key} className="flex items-center gap-1.5">
                <StateIcon
                  className={textColorForStep({
                    state: row.state,
                    outcome: row.outcome,
                    missingDependencies: [],
                  })}
                  state={row.state}
                  outcome={row.outcome}
                  missingDependencies={[]}
                />
                {row.count} {row.count === 1 ? "step" : "steps"} {row.label}
              </li>
            ))}
          </ul>
        )}

        <MissingStepDependencies missingDependencies={missingDependencies} build={build} />
      </div>
    </HoverCard.Content>
  );
}
